'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import { MouseEvent, Suspense, use, useOptimistic, useRef, useState, useTransition } from 'react';
import useSWR from 'swr';
import { useIsClient } from 'usehooks-ts';
import PremiumAccessButton from '@/app/[locale]/(worlds)/[roomId]/components/PremiumAccessButton';
import GuestFollowCounts, { GuestFollowCountsRef } from '@/app/[locale]/(worlds)/profile/[identifier]/components/GuestFollowCounts';
import { socialLinksMap } from '@/app/[locale]/(worlds)/profile/components/EditUserAbout';
import ProfileAvatar from '@/app/[locale]/(worlds)/profile/components/ProfileAvatar';
import FollowCountsDialogs from '@/components/FollowCountsDialogs';
import PopoverMenu from '@/components/PopoverMenu';
import TipModal from '@/components/TipModal';
import VerifiedBadge from '@/components/VerifiedBadge';
import ViverseButton from '@/components/ViverseButton';
import ViverseIconButton from '@/components/ViverseIconButton';
import IconEye from '@/components/assets/icon_eye.svg';
import IconFB from '@/components/assets/icon_fb.svg';
import IconFriend from '@/components/assets/icon_friend.svg';
import IconLink from '@/components/assets/icon_link.svg';
import IconNotification from '@/components/assets/icon_notification.svg';
import IconNotificationOutline from '@/components/assets/icon_notification_outline.svg';
import IconShare from '@/components/assets/icon_share.svg';
import IconTwitter from '@/components/assets/icon_twitter.svg';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useAuthContext } from '@/context/AuthContext';
import { useConfigsContext } from '@/context/ConfigsContext';
import { OwnerProfileContext } from '@/context/OwnerProfileContext';
import { useUAContext } from '@/context/UAContext';
import usePremiumPlansData from '@/hooks/usePremiumPlansData';
import { useShareLink } from '@/hooks/useShareLink';
import { Link } from '@/i18n/navigation';
import { hasVerifiedCreatorBadge } from '@/lib/cms';
import { checkIsFollowed } from '@/lib/community';
import { getWorldsDomain } from '@/lib/configs';
import { cn } from '@/lib/utils';
import defaultHeadIcon from '@/public/images/creator-intro/avatar-2.png';
import { getUserEntryPassByCreator, UserProfile } from '@/services/cms';
import { AuthenticatedCommunityIdentity, getAuthenticatedFollowingsList, postAuthenticatedFollow, postAuthenticatedUnfollow } from '@/services/community';
type ProfileIntroProps = {
  userProfile: Partial<UserProfile>;
  guestAccountPromise: Promise<UserProfile | undefined>;
  isPersonaProfilePage?: boolean;
};
const ProfileIntroInner = t0 => {
  const $ = _c(202);
  const {
    userProfile,
    guestAccountPromise,
    isPersonaProfilePage: t1
  } = t0;
  const isPersonaProfilePage = t1 === undefined ? false : t1;
  const t = useTranslations();
  const {
    isMobileApp
  } = useUAContext();
  const {
    store
  } = useConfigsContext();
  const {
    currentUserId,
    handleLogin,
    isSignedIn
  } = useAuthContext();
  const ownerProfileContext = isPersonaProfilePage ? null : use(OwnerProfileContext);
  const setIsPublicView = ownerProfileContext?.setIsPublicView;
  const {
    display_name: displayName,
    community_user_id: communityUserId,
    active_avatar: activeAvatar,
    htc_account_id: htcAccountId,
    social_links: socialLinks,
    accept_tip: isAcceptTip
  } = userProfile;
  const htcToken = store?.credentials?.htcToken;
  const profileId = store?.profile?.id;
  const preferredUsername = store?.profile?.userId;
  let t2;
  if ($[0] !== htcToken || $[1] !== preferredUsername) {
    t2 = htcToken && preferredUsername ? {
      htcToken,
      preferredUsername
    } : undefined;
    $[0] = htcToken;
    $[1] = preferredUsername;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const authenticatedIdentity = t2;
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const guestAccountData = use(guestAccountPromise);
  let t3;
  if ($[3] !== authenticatedIdentity) {
    t3 = () => getAuthenticatedFollowingsList(authenticatedIdentity);
    $[3] = authenticatedIdentity;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const {
    data: followingsData,
    mutate: mutateFollowingsData
  } = useSWR(authenticatedIdentity ? "getFollowingsData" : null, t3, {
    onSuccess: data => {
      const following = checkIsFollowed(data, communityUserId);
      setIsFollowed(!!following);
      setIsButtonEnabled(true);
    }
  });
  const {
    planList
  } = usePremiumPlansData(htcAccountId, true);
  const showPremiumButton = !isMobileApp && !!planList?.data?.products?.items?.length;
  const canFetchUserPurchase = showPremiumButton && htcToken && profileId;
  let t4;
  if ($[5] !== htcAccountId || $[6] !== htcToken || $[7] !== profileId) {
    t4 = () => getUserEntryPassByCreator(htcToken, profileId, htcAccountId).then(_temp);
    $[5] = htcAccountId;
    $[6] = htcToken;
    $[7] = profileId;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const {
    data: hasUserPurchased
  } = useSWR(canFetchUserPurchase ? "hasUserPurchased" : null, t4);
  const [isFollowed, t5] = useState(isSignedIn);
  const setIsFollowed = t5;
  const [optimisticIsFollowed, setOptimisticIsFollowed] = useOptimistic(isFollowed);
  const [isPending, startTransition] = useTransition();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileOpen, setMobileMenuOpen] = useState(false);
  const guestFollowCountRef = useRef(null);
  const isClient = useIsClient();
  const isVisitingOtherUserProfile = !!communityUserId && currentUserId !== communityUserId;
  const showFollowButton = isVisitingOtherUserProfile && !!guestAccountData?.community_user_id;
  const showEditButton = isClient && !isVisitingOtherUserProfile && !isPersonaProfilePage;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = getWorldsDomain();
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const worldsDomain = t6;
  const profileLink = `https://${worldsDomain}/profile/${communityUserId || store?.profile?.userId}`;
  const {
    copyLink,
    createSharePlatformLink
  } = useShareLink(isPersonaProfilePage ? profileLink + "/persona" : profileLink);
  let t7;
  if ($[10] !== copyLink) {
    t7 = () => {
      copyLink();
      setMenuOpen(false);
      setMobileMenuOpen(false);
    };
    $[10] = copyLink;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const handleCopyLink = t7;
  let t8;
  if ($[12] !== authenticatedIdentity || $[13] !== communityUserId || $[14] !== handleLogin || $[15] !== htcAccountId || $[16] !== isSignedIn || $[17] !== mutateFollowingsData || $[18] !== optimisticIsFollowed || $[19] !== setIsFollowed || $[20] !== setOptimisticIsFollowed) {
    t8 = () => {
      if (!isSignedIn) {
        handleLogin();
        return;
      }
      if (!authenticatedIdentity || !htcAccountId || !communityUserId) {
        return;
      }
      const following_0 = optimisticIsFollowed;
      const action = following_0 ? postAuthenticatedUnfollow : postAuthenticatedFollow;
      startTransition(async () => {
        setOptimisticIsFollowed(!following_0);
        const postResponse = await action(authenticatedIdentity, communityUserId);
        if (postResponse?.status !== 200) {
          console.log("Follow user failed");
          return;
        }
        setIsFollowed(!following_0);
        mutateFollowingsData();
        guestFollowCountRef.current?.mutateFollowCounts();
      });
    };
    $[12] = authenticatedIdentity;
    $[13] = communityUserId;
    $[14] = handleLogin;
    $[15] = htcAccountId;
    $[16] = isSignedIn;
    $[17] = mutateFollowingsData;
    $[18] = optimisticIsFollowed;
    $[19] = setIsFollowed;
    $[20] = setOptimisticIsFollowed;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const handleToggleFollow = t8;
  let t9;
  if ($[22] !== t) {
    t9 = isFollowed_0 => {
      if (!isFollowed_0) {
        return t("profile-page.intro-follow-action");
      }
      return t("profile-page.intro-unfollow-action");
    };
    $[22] = t;
    $[23] = t9;
  } else {
    t9 = $[23];
  }
  const getButtonChildren = t9;
  const handleClickIcon = _temp2;
  const t10 = activeAvatar?.head_icon_url || defaultHeadIcon.src;
  let t11;
  if ($[24] !== t10) {
    t11 = <ProfileAvatar variant="public" avatarUrl={t10} />;
    $[24] = t10;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== displayName) {
    t12 = <span className="truncate">{displayName}</span>;
    $[26] = displayName;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== userProfile) {
    t13 = hasVerifiedCreatorBadge(userProfile);
    $[28] = userProfile;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== t13) {
    t14 = <VerifiedBadge className="md:size-6" isVerified={t13} />;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t12 || $[33] !== t14) {
    t15 = <h3 className="header-4 md:header-3 flex min-w-0 items-center justify-center gap-1 text-center md:justify-start md:gap-2 md:text-left">{t12}{t14}</h3>;
    $[32] = t12;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== communityUserId) {
    t16 = <p className="body-2 max-md:text-center">@{communityUserId}</p>;
    $[35] = communityUserId;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t15 || $[38] !== t16) {
    t17 = <div className="flex min-w-0 flex-col">{t15}{t16}</div>;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== isClient || $[41] !== isVisitingOtherUserProfile) {
    t18 = isClient && (isVisitingOtherUserProfile ? <GuestFollowCounts ref={guestFollowCountRef} setIsButtonEnabled={setIsButtonEnabled} /> : <FollowCountsDialogs />);
    $[40] = isClient;
    $[41] = isVisitingOtherUserProfile;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t18) {
    t19 = <div className="flex gap-2 max-md:m-auto">{t18}</div>;
    $[43] = t18;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== socialLinks) {
    t20 = socialLinks?.map(_temp3);
    $[45] = socialLinks;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== t20) {
    t21 = <div className="flex items-center gap-2 max-md:m-auto">{t20}</div>;
    $[47] = t20;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== t17 || $[50] !== t19 || $[51] !== t21) {
    t22 = <div className="flex min-w-0 flex-col gap-2">{t17}{t19}{t21}</div>;
    $[49] = t17;
    $[50] = t19;
    $[51] = t21;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] !== t11 || $[54] !== t22) {
    t23 = <div className="relative flex min-w-0 flex-col items-center gap-2 md:flex-row md:items-start md:gap-4">{t11}{t22}</div>;
    $[53] = t11;
    $[54] = t22;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== handleToggleFollow || $[57] !== isButtonEnabled || $[58] !== isPending || $[59] !== optimisticIsFollowed || $[60] !== showFollowButton) {
    t24 = showFollowButton && <ViverseIconButton data-component="follow-button" variant="filled" size="S" className="disabled:pointer-events-auto disabled:cursor-not-allowed" onClick={handleToggleFollow} disabled={!isButtonEnabled || isPending}>{optimisticIsFollowed ? <IconNotificationOutline className="[&>path]:fill-vive-primary-20 size-5" /> : <IconNotification className="size-5" />}</ViverseIconButton>;
    $[56] = handleToggleFollow;
    $[57] = isButtonEnabled;
    $[58] = isPending;
    $[59] = optimisticIsFollowed;
    $[60] = showFollowButton;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== displayName || $[63] !== htcAccountId || $[64] !== isAcceptTip) {
    t25 = isAcceptTip && <TipModal recipientName={displayName} creatorId={htcAccountId} dialogTrigger={<ViverseIconButton data-component="tip-button" variant="filled" size="S"><IconFriend className="[&>path]:fill-vive-primary-20 size-5" /></ViverseIconButton>} />;
    $[62] = displayName;
    $[63] = htcAccountId;
    $[64] = isAcceptTip;
    $[65] = t25;
  } else {
    t25 = $[65];
  }
  let t26;
  if ($[66] !== setIsPublicView || $[67] !== showEditButton || $[68] !== t) {
    t26 = showEditButton && <ViverseButton variant="secondary" size="small" className="m-auto h-fit px-4 md:m-[inherit]" onClick={() => setIsPublicView?.(false)}><IconEye className="mr-2 size-6" />{t("profile-page.intro.private-profile")}</ViverseButton>;
    $[66] = setIsPublicView;
    $[67] = showEditButton;
    $[68] = t;
    $[69] = t26;
  } else {
    t26 = $[69];
  }
  let t27;
  if ($[70] !== followingsData) {
    t27 = !!followingsData && <ViverseIconButton data-component="share-menu" variant="filled" size="S"><IconShare className="[&>path]:fill-vive-primary-20 size-5" /></ViverseIconButton>;
    $[70] = followingsData;
    $[71] = t27;
  } else {
    t27 = $[71];
  }
  let t28;
  if ($[72] !== t27) {
    t28 = <PopoverTrigger asChild={true} onClick={handleClickIcon}>{t27}</PopoverTrigger>;
    $[72] = t27;
    $[73] = t28;
  } else {
    t28 = $[73];
  }
  let t29;
  if ($[74] !== createSharePlatformLink) {
    t29 = createSharePlatformLink("facebook");
    $[74] = createSharePlatformLink;
    $[75] = t29;
  } else {
    t29 = $[75];
  }
  let t30;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <IconFB className="fill-vive-primary-20 size-6" />;
    $[76] = t30;
  } else {
    t30 = $[76];
  }
  let t31;
  if ($[77] !== t) {
    t31 = t("profile-page.share-facebook");
    $[77] = t;
    $[78] = t31;
  } else {
    t31 = $[78];
  }
  let t32;
  if ($[79] !== t31) {
    t32 = <PopoverMenu.Item>{t30}<div className="body-1 text-inherit">{t31}</div></PopoverMenu.Item>;
    $[79] = t31;
    $[80] = t32;
  } else {
    t32 = $[80];
  }
  let t33;
  if ($[81] !== t29 || $[82] !== t32) {
    t33 = <Link href={t29} target="_blank">{t32}</Link>;
    $[81] = t29;
    $[82] = t32;
    $[83] = t33;
  } else {
    t33 = $[83];
  }
  let t34;
  if ($[84] !== createSharePlatformLink || $[85] !== displayName) {
    t34 = createSharePlatformLink("twitter", displayName);
    $[84] = createSharePlatformLink;
    $[85] = displayName;
    $[86] = t34;
  } else {
    t34 = $[86];
  }
  let t35;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <IconTwitter className="fill-vive-primary-20 size-6" />;
    $[87] = t35;
  } else {
    t35 = $[87];
  }
  let t36;
  if ($[88] !== t) {
    t36 = t("profile-page.share-twitter");
    $[88] = t;
    $[89] = t36;
  } else {
    t36 = $[89];
  }
  let t37;
  if ($[90] !== t36) {
    t37 = <PopoverMenu.Item>{t35}<div className="body-1 text-inherit">{t36}</div></PopoverMenu.Item>;
    $[90] = t36;
    $[91] = t37;
  } else {
    t37 = $[91];
  }
  let t38;
  if ($[92] !== t34 || $[93] !== t37) {
    t38 = <Link href={t34} target="_blank">{t37}</Link>;
    $[92] = t34;
    $[93] = t37;
    $[94] = t38;
  } else {
    t38 = $[94];
  }
  let t39;
  if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <IconLink className="fill-vive-primary-20 size-6" />;
    $[95] = t39;
  } else {
    t39 = $[95];
  }
  let t40;
  if ($[96] !== t) {
    t40 = t("profile-page.share-copy-link");
    $[96] = t;
    $[97] = t40;
  } else {
    t40 = $[97];
  }
  let t41;
  if ($[98] !== t40) {
    t41 = <div className="body-1 text-inherit">{t40}</div>;
    $[98] = t40;
    $[99] = t41;
  } else {
    t41 = $[99];
  }
  let t42;
  if ($[100] !== handleCopyLink || $[101] !== t41) {
    t42 = <PopoverMenu.Item onClick={handleCopyLink}>{t39}{t41}</PopoverMenu.Item>;
    $[100] = handleCopyLink;
    $[101] = t41;
    $[102] = t42;
  } else {
    t42 = $[102];
  }
  let t43;
  if ($[103] !== t33 || $[104] !== t38 || $[105] !== t42) {
    t43 = <PopoverContent align="center" className="mt-2 border-none bg-transparent p-0"><PopoverMenu><PopoverMenu.Group>{t33}{t38}{t42}</PopoverMenu.Group></PopoverMenu></PopoverContent>;
    $[103] = t33;
    $[104] = t38;
    $[105] = t42;
    $[106] = t43;
  } else {
    t43 = $[106];
  }
  let t44;
  if ($[107] !== mobileOpen || $[108] !== t28 || $[109] !== t43) {
    t44 = <Popover open={mobileOpen} onOpenChange={setMobileMenuOpen}>{t28}{t43}</Popover>;
    $[107] = mobileOpen;
    $[108] = t28;
    $[109] = t43;
    $[110] = t44;
  } else {
    t44 = $[110];
  }
  let t45;
  if ($[111] !== canFetchUserPurchase || $[112] !== communityUserId || $[113] !== hasUserPurchased || $[114] !== showPremiumButton) {
    t45 = showPremiumButton && <PremiumAccessButton data-component="premium-access-button" isPaid={canFetchUserPurchase ? hasUserPurchased : false} page="profile" communityUserId={communityUserId} />;
    $[111] = canFetchUserPurchase;
    $[112] = communityUserId;
    $[113] = hasUserPurchased;
    $[114] = showPremiumButton;
    $[115] = t45;
  } else {
    t45 = $[115];
  }
  let t46;
  if ($[116] !== t24 || $[117] !== t25 || $[118] !== t26 || $[119] !== t44 || $[120] !== t45) {
    t46 = <div className="flex flex-wrap justify-center gap-2 md:hidden">{t24}{t25}{t26}{t44}{t45}</div>;
    $[116] = t24;
    $[117] = t25;
    $[118] = t26;
    $[119] = t44;
    $[120] = t45;
    $[121] = t46;
  } else {
    t46 = $[121];
  }
  let t47;
  if ($[122] !== t23 || $[123] !== t46) {
    t47 = <div className="flex min-w-0 flex-col gap-4 md:flex-row md:justify-between">{t23}{t46}</div>;
    $[122] = t23;
    $[123] = t46;
    $[124] = t47;
  } else {
    t47 = $[124];
  }
  let t48;
  if ($[125] !== getButtonChildren || $[126] !== handleToggleFollow || $[127] !== isButtonEnabled || $[128] !== isPending || $[129] !== optimisticIsFollowed || $[130] !== showFollowButton) {
    t48 = showFollowButton && <ViverseButton data-component="follow-button" className="h-12 gap-2 disabled:pointer-events-auto disabled:cursor-not-allowed" variant="secondary" onClick={handleToggleFollow} disabled={!isButtonEnabled || isPending}>{optimisticIsFollowed ? <IconNotificationOutline className="[&>path]:stroke-vive-primary-20 size-6" /> : <IconNotification className="[&>path]:fill-vive-primary-20 size-6" />}<span className="text-vive-primary-20 buttonM-omni-styles">{getButtonChildren(optimisticIsFollowed)}</span></ViverseButton>;
    $[125] = getButtonChildren;
    $[126] = handleToggleFollow;
    $[127] = isButtonEnabled;
    $[128] = isPending;
    $[129] = optimisticIsFollowed;
    $[130] = showFollowButton;
    $[131] = t48;
  } else {
    t48 = $[131];
  }
  let t49;
  if ($[132] !== displayName || $[133] !== htcAccountId || $[134] !== isAcceptTip || $[135] !== t) {
    t49 = isAcceptTip && <TipModal recipientName={displayName} creatorId={htcAccountId} dialogTrigger={<ViverseButton data-component="tip-button" className="h-12 gap-2" variant="secondary"><IconFriend className="[&>path]:fill-vive-primary-20 size-6" /><span className="text-vive-primary-20 buttonM-omni-styles">{t("profile-page.tip.button")}</span></ViverseButton>} />;
    $[132] = displayName;
    $[133] = htcAccountId;
    $[134] = isAcceptTip;
    $[135] = t;
    $[136] = t49;
  } else {
    t49 = $[136];
  }
  let t50;
  if ($[137] !== setIsPublicView || $[138] !== showEditButton || $[139] !== t) {
    t50 = showEditButton && <ViverseButton className="h-12 gap-2" variant="secondary" onClick={() => setIsPublicView?.(false)}><span className="text-vive-primary-20 buttonM-omni-styles flex items-center"><IconEye className="mr-2 size-6" />{t("profile-page.intro.private-profile")}</span></ViverseButton>;
    $[137] = setIsPublicView;
    $[138] = showEditButton;
    $[139] = t;
    $[140] = t50;
  } else {
    t50 = $[140];
  }
  const t51 = !followingsData;
  let t52;
  if ($[141] !== t51) {
    t52 = cn("h-12 gap-2", {
      hidden: t51
    });
    $[141] = t51;
    $[142] = t52;
  } else {
    t52 = $[142];
  }
  let t53;
  if ($[143] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <IconShare className="size-6" />;
    $[143] = t53;
  } else {
    t53 = $[143];
  }
  let t54;
  if ($[144] !== t) {
    t54 = t("card-share-popover.title-share");
    $[144] = t;
    $[145] = t54;
  } else {
    t54 = $[145];
  }
  let t55;
  if ($[146] !== t54) {
    t55 = <span className="text-vive-primary-20 buttonM-omni-styles">{t54}</span>;
    $[146] = t54;
    $[147] = t55;
  } else {
    t55 = $[147];
  }
  let t56;
  if ($[148] !== t52 || $[149] !== t55) {
    t56 = <PopoverTrigger asChild={true} onClick={handleClickIcon}><ViverseButton data-component="share-menu" className={t52} variant="secondary">{t53}{t55}</ViverseButton></PopoverTrigger>;
    $[148] = t52;
    $[149] = t55;
    $[150] = t56;
  } else {
    t56 = $[150];
  }
  let t57;
  if ($[151] !== createSharePlatformLink) {
    t57 = createSharePlatformLink("facebook");
    $[151] = createSharePlatformLink;
    $[152] = t57;
  } else {
    t57 = $[152];
  }
  let t58;
  if ($[153] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <IconFB className="fill-vive-primary-20 size-6" />;
    $[153] = t58;
  } else {
    t58 = $[153];
  }
  let t59;
  if ($[154] !== t) {
    t59 = t("profile-page.share-facebook");
    $[154] = t;
    $[155] = t59;
  } else {
    t59 = $[155];
  }
  let t60;
  if ($[156] !== t59) {
    t60 = <PopoverMenu.Item>{t58}<div className="body-1 text-inherit">{t59}</div></PopoverMenu.Item>;
    $[156] = t59;
    $[157] = t60;
  } else {
    t60 = $[157];
  }
  let t61;
  if ($[158] !== t57 || $[159] !== t60) {
    t61 = <Link href={t57} target="_blank">{t60}</Link>;
    $[158] = t57;
    $[159] = t60;
    $[160] = t61;
  } else {
    t61 = $[160];
  }
  let t62;
  if ($[161] !== createSharePlatformLink || $[162] !== displayName) {
    t62 = createSharePlatformLink("twitter", displayName);
    $[161] = createSharePlatformLink;
    $[162] = displayName;
    $[163] = t62;
  } else {
    t62 = $[163];
  }
  let t63;
  if ($[164] === Symbol.for("react.memo_cache_sentinel")) {
    t63 = <IconTwitter className="fill-vive-primary-20 size-6" />;
    $[164] = t63;
  } else {
    t63 = $[164];
  }
  let t64;
  if ($[165] !== t) {
    t64 = t("profile-page.share-twitter");
    $[165] = t;
    $[166] = t64;
  } else {
    t64 = $[166];
  }
  let t65;
  if ($[167] !== t64) {
    t65 = <PopoverMenu.Item>{t63}<div className="body-1 text-inherit">{t64}</div></PopoverMenu.Item>;
    $[167] = t64;
    $[168] = t65;
  } else {
    t65 = $[168];
  }
  let t66;
  if ($[169] !== t62 || $[170] !== t65) {
    t66 = <Link href={t62} target="_blank">{t65}</Link>;
    $[169] = t62;
    $[170] = t65;
    $[171] = t66;
  } else {
    t66 = $[171];
  }
  let t67;
  if ($[172] === Symbol.for("react.memo_cache_sentinel")) {
    t67 = <IconLink className="fill-vive-primary-20 size-6" />;
    $[172] = t67;
  } else {
    t67 = $[172];
  }
  let t68;
  if ($[173] !== t) {
    t68 = t("profile-page.share-copy-link");
    $[173] = t;
    $[174] = t68;
  } else {
    t68 = $[174];
  }
  let t69;
  if ($[175] !== t68) {
    t69 = <div className="body-1 text-inherit">{t68}</div>;
    $[175] = t68;
    $[176] = t69;
  } else {
    t69 = $[176];
  }
  let t70;
  if ($[177] !== handleCopyLink || $[178] !== t69) {
    t70 = <PopoverMenu.Item onClick={handleCopyLink}>{t67}{t69}</PopoverMenu.Item>;
    $[177] = handleCopyLink;
    $[178] = t69;
    $[179] = t70;
  } else {
    t70 = $[179];
  }
  let t71;
  if ($[180] !== t61 || $[181] !== t66 || $[182] !== t70) {
    t71 = <PopoverContent align="end" className="mt-2 border-none bg-transparent p-0"><PopoverMenu><PopoverMenu.Group>{t61}{t66}{t70}</PopoverMenu.Group></PopoverMenu></PopoverContent>;
    $[180] = t61;
    $[181] = t66;
    $[182] = t70;
    $[183] = t71;
  } else {
    t71 = $[183];
  }
  let t72;
  if ($[184] !== menuOpen || $[185] !== t56 || $[186] !== t71) {
    t72 = <Popover open={menuOpen} onOpenChange={setMenuOpen}>{t56}{t71}</Popover>;
    $[184] = menuOpen;
    $[185] = t56;
    $[186] = t71;
    $[187] = t72;
  } else {
    t72 = $[187];
  }
  let t73;
  if ($[188] !== canFetchUserPurchase || $[189] !== communityUserId || $[190] !== hasUserPurchased || $[191] !== showPremiumButton) {
    t73 = showPremiumButton && <PremiumAccessButton data-component="premium-access-button" isPaid={canFetchUserPurchase ? hasUserPurchased : false} page="profile" communityUserId={communityUserId} />;
    $[188] = canFetchUserPurchase;
    $[189] = communityUserId;
    $[190] = hasUserPurchased;
    $[191] = showPremiumButton;
    $[192] = t73;
  } else {
    t73 = $[192];
  }
  let t74;
  if ($[193] !== t48 || $[194] !== t49 || $[195] !== t50 || $[196] !== t72 || $[197] !== t73) {
    t74 = <div className="hidden min-w-fit justify-end gap-2 md:flex">{t48}{t49}{t50}{t72}{t73}</div>;
    $[193] = t48;
    $[194] = t49;
    $[195] = t50;
    $[196] = t72;
    $[197] = t73;
    $[198] = t74;
  } else {
    t74 = $[198];
  }
  let t75;
  if ($[199] !== t47 || $[200] !== t74) {
    t75 = <section data-section="profile-intro" className="flex justify-center px-4 pt-2 pb-4 md:justify-between md:px-8 md:py-4 lg:px-18">{t47}{t74}</section>;
    $[199] = t47;
    $[200] = t74;
    $[201] = t75;
  } else {
    t75 = $[201];
  }
  return t75;
};
const ProfileIntro = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Suspense><ProfileIntroInner {...props} /></Suspense>;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export default ProfileIntro;
function _temp(res) {
  return !!(res?.results.result && res.results.result.length > 0);
}
function _temp2(e) {
  e.stopPropagation();
  e.nativeEvent.preventDefault();
}
function _temp3(link) {
  const Icon = socialLinksMap[link.key].Icon;
  return <Link href={link.value} key={link.key} target="_blank"><ViverseButton variant="icon2" size="icon" className="size-[30px]"><Icon className="size-4" /></ViverseButton></Link>;
}